import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Mail, Check, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await api.get('/contact/messages');
        setMessages(response.data);
      } catch (error) {
        console.error('Error fetching messages:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMessages();
  }, []);

  const handleMarkRead = async (messageId) => {
    try {
      await api.put(`/contact/messages/${messageId}/read`);
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, is_read: true } : m
      ));
      toast.success('Mensaje marcado como leído');
    } catch (error) {
      toast.error('Error al actualizar el mensaje');
    }
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div className="pt-20 min-h-screen bg-[hsl(var(--sage-light))]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link 
            to="/admin" 
            className="p-2 rounded-full hover:bg-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[hsl(var(--text-secondary))]" strokeWidth={1.5} />
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-heading text-[hsl(var(--text-primary))]" data-testid="admin-messages-title">
              Mensajes de contacto
            </h1>
            <p className="text-[hsl(var(--text-secondary))]">
              {unreadCount} sin leer de {messages.length} totales
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Message List */}
          <div className="lg:col-span-1 space-y-3">
            {loading ? (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-[hsl(var(--accent))] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              </div>
            ) : messages.length === 0 ? (
              <div className="card-soft text-center p-8">
                <MessageSquare className="w-10 h-10 text-[hsl(var(--sage))] mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-[hsl(var(--text-secondary))]">No hay mensajes</p>
              </div>
            ) : (
              messages.map((message) => (
                <button
                  key={message.id}
                  onClick={() => setSelectedMessage(message)}
                  className={`w-full text-left card-soft p-4 transition-all ${
                    selectedMessage?.id === message.id 
                      ? 'ring-2 ring-[hsl(var(--accent))]'
                      : ''
                  } ${!message.is_read ? 'border-l-4 border-l-[hsl(var(--accent))]' : ''}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className={`font-medium truncate ${
                      !message.is_read ? 'text-[hsl(var(--text-primary))]' : 'text-[hsl(var(--text-secondary))]'
                    }`}>
                      {message.name}
                    </p>
                    {!message.is_read && (
                      <span className="w-2 h-2 rounded-full bg-[hsl(var(--accent))] flex-shrink-0 mt-2"></span>
                    )}
                  </div>
                  <p className="text-sm text-[hsl(var(--text-tertiary))] truncate mb-1">
                    {message.subject}
                  </p>
                  <p className="text-xs text-[hsl(var(--text-tertiary))]">
                    {message.created_at ? new Date(message.created_at).toLocaleDateString('es-ES') : ''}
                  </p>
                </button>
              ))
            )}
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2">
            {selectedMessage ? (
              <div className="card-soft">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-heading text-[hsl(var(--text-primary))] mb-1">
                      {selectedMessage.subject}
                    </h2>
                    <p className="text-sm text-[hsl(var(--text-tertiary))] flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {selectedMessage.created_at ? new Date(selectedMessage.created_at).toLocaleString('es-ES') : ''}
                    </p>
                  </div>
                  {!selectedMessage.is_read && (
                    <Button 
                      onClick={() => handleMarkRead(selectedMessage.id)}
                      className="btn-outline" 
                      size="sm"
                    >
                      <Check className="w-4 h-4 mr-2" /> Marcar leído
                    </Button>
                  )}
                </div>

                <div className="p-4 bg-[hsl(var(--sage-light))] rounded-xl mb-6">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[hsl(var(--accent))]/10 flex items-center justify-center">
                      <span className="text-[hsl(var(--accent))] font-medium">
                        {selectedMessage.name?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-[hsl(var(--text-primary))]">
                        {selectedMessage.name}
                      </p>
                      <p className="text-sm text-[hsl(var(--text-tertiary))] flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {selectedMessage.email}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="prose max-w-none">
                  <p className="text-[hsl(var(--text-secondary))] whitespace-pre-wrap leading-relaxed">
                    {selectedMessage.message}
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-[hsl(var(--border))]">
                  <a 
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="btn-gold inline-flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Responder por email
                  </a>
                </div>
              </div>
            ) : (
              <div className="card-soft text-center p-12">
                <MessageSquare className="w-12 h-12 text-[hsl(var(--sage))] mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-[hsl(var(--text-secondary))]">
                  Selecciona un mensaje para ver los detalles
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;
